import { useReducer, useRef, useCallback } from 'react';
import type { EffectHandler } from '../shared/flow-store';

export interface FlowReducerConfig<
  S extends { step: string },
  A extends { type: string },
  D,
> {
  reducer: (state: S, action: A) => S;
  initialState: S;
  effects: Partial<Record<string, EffectHandler<S, A, D>>>;
  deriveFailure: (action: A, err: unknown) => A;
  deps: D;
}

export function useFlowReducer<
  S extends { step: string },
  A extends { type: string },
  D,
>(config: FlowReducerConfig<S, A, D>): [S, (action: A) => void] {
  const [state, rawDispatch] = useReducer(config.reducer, config.initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const inflightRef = useRef(new Set<string>());
  const configRef = useRef(config);
  configRef.current = config;

  const dispatch = useCallback((action: A) => {
    const prev = stateRef.current;
    rawDispatch(action);

    const cfg = configRef.current;
    const handler = cfg.effects[action.type as string];
    if (!handler || inflightRef.current.has(action.type)) return;

    inflightRef.current.add(action.type);
    handler(action, prev, dispatch, cfg.deps)
      .catch((err: unknown) => dispatch(cfg.deriveFailure(action, err)))
      .finally(() => inflightRef.current.delete(action.type));
  }, []);

  return [state, dispatch];
}
